import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote } from 'lucide-react';
import { defaultTestimonials } from '@/lib/defaults';
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'testimonials'));
        if (!snap.empty) {
          const items = snap.docs.map(d => ({ ...d.data(), id: d.id })).filter(t => t.enabled !== false);
          if (items.length > 0) setTestimonials(items);
        }
      } catch (e) { /* use defaults */ }
    }
    load();
  }, []);
  return (
    <div data-testid="testimonials-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Testimonials</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">What Our Students Say</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">
            Don't just take our word for it. Here's what students and parents say about their Singvolve experience.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-8">
          {testimonials.filter(t => t.enabled !== false).map((t, i) => (
            <motion.div key={t.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full">
                <CardContent className="p-8 relative">
                  <Quote className="absolute top-6 right-6 h-8 w-8 text-[#F5F0F7]" strokeWidth={1.5} />
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                  <p className="text-[#2D1B36] leading-relaxed mb-6 italic text-base">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
                    <div className="w-10 h-10 rounded-full bg-[#F5F0F7] flex items-center justify-center text-[#4B1D5E] font-semibold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-[#2D1B36] text-sm">{t.name}</p>
                      <p className="text-xs text-[#8A7E90]">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
